import api from '../../../api/axios';

// ==========================================
// VALIDADOR INTELIGENTE DE JSON
// Calcula la línea y columna exacta del error
// ==========================================
const validateMapJson = (jsonString, routeTitle) => {
  if (!jsonString || jsonString.trim() === '') {
    return null; // Si está vacío, es válido (se omitirá el mapa)
  }

  try {
    const parsed = JSON.parse(jsonString);

    // 1. Validación de Estructura Mínima
    if (!parsed.start || !Array.isArray(parsed.start) || parsed.start.length !== 2) {
      return `La ruta "${routeTitle}" debe tener "start" como un array de 2 números [latitud, longitud].`;
    }
    if (!parsed.path || !Array.isArray(parsed.path) || parsed.path.length < 2) {
      return `La ruta "${routeTitle}" debe tener "path" con al menos 2 puntos de coordenadas.`;
    }

    // 2. Validación de que sean números (opcional pero recomendado)
    const isValidCoord = (coord) => Array.isArray(coord) && coord.length === 2 && typeof coord[0] === 'number' && typeof coord[1] === 'number';
    
    if (!isValidCoord(parsed.start)) return `Las coordenadas de "start" en "${routeTitle}" deben ser números.`;
    if (!isValidCoord(parsed.end)) return `Las coordenadas de "end" en "${routeTitle}" deben ser números.`;
    
    for (let i = 0; i < parsed.path.length; i++) {
      if (!isValidCoord(parsed.path[i])) {
        return `El punto ${i + 1} del "path" en "${routeTitle}" no tiene el formato [latitud, longitud].`;
      }
    }

    return null; // Todo perfecto
  } catch (error) {
    // 3. Extracción inteligente de la línea y columna del error nativo de JS
    const match = error.message.match(/position (\d+)/);
    if (match) {
      const pos = parseInt(match[1], 10);
      const lines = jsonString.substring(0, pos).split('\n');
      const line = lines.length;
      const col = lines[lines.length - 1].length + 1;
      
      // Limpiamos el mensaje nativo para que sea más amigable
      const cleanError = error.message.replace(/ at position \d+/, '').replace('Unexpected token', 'Carácter inesperado');
      
      return `Error de sintaxis en "${routeTitle}" (Línea ${line}, Columna ${col}): ${cleanError}. Revisa comas faltantes, comillas simples (usa "") o corchetes.`;
    }
    
    return `Error de sintaxis JSON en "${routeTitle}": ${error.message}`;
  }
};

export const routesService = {
  /**
   * Obtener rutas del template
   */
  getRoutes: async (slug) => {
    try {
      const response = await api.get(`/admin/template/${slug}`);
      const fetchedRoutes = response.data.routes || [];
      
      return fetchedRoutes.map((route, index) => ({
        title: route.title || `Ruta ${index + 1}`,
        description: route.description || '',
        image: route.image || '',
        link: route.link || '',
        // Si viene como objeto del backend, lo convertimos a string bonito para el textarea
        map_data: route.map_data 
          ? (typeof route.map_data === 'object' ? JSON.stringify(route.map_data, null, 2) : route.map_data)
          : '',
        file: null,
      }));
    } catch (error) {
      console.error('Error al cargar las rutas:', error);
      throw error;
    }
  },

  /**
   * Guardar rutas con imágenes
   */
  saveRoutes: async (slug, routes) => {
    const formData = new FormData();

    const filteredRoutes = routes.filter(route => 
      route.description && route.description.trim() !== ""
    );

    if (filteredRoutes.length === 0) {
      throw new Error('Debes agregar al menos una ruta con descripción antes de guardar.');
    }

    const routesData = filteredRoutes.map(route => {
      // 🚀 AQUÍ USAMOS EL VALIDADOR INTELIGENTE
      const validationError = validateMapJson(route.map_data, route.title);
      if (validationError) {
        throw new Error(validationError); // Esto será capturado por el componente y mostrado en alerta
      }

      return {
        title: route.title || '',
        description: route.description || '',
        link: route.link || '',
        // Si es válido, lo guardamos como objeto JSON real en el backend
        map_data: route.map_data && route.map_data.trim() !== '' ? JSON.parse(route.map_data) : null
      };
    });

    filteredRoutes.forEach((route, index) => {
      if (route.file) {
        formData.append(`routes[${index}][image]`, route.file);
      }
    });

    formData.append('routes', JSON.stringify(routesData));

    const response = await api.put(`/admin/template/${slug}/routes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  },

  /**
   * Crear ruta vacía
   */
  createEmptyRoute: (index) => ({
    title: `Ruta ${index + 1}`,
    description: '',
    image: '',
    link: '',
    map_data: '', 
    file: null,
  }),

  /**
   * Validar ruta en caliente (mientras el usuario escribe o antes de guardar)
   */
  validateRoute: (route) => {
    if (!route.description || route.description.trim() === '') {
      return 'La descripción de la ruta es obligatoria.';
    }
    
    // Usamos el mismo validador inteligente
    return validateMapJson(route.map_data, route.title);
  },

  MAX_ROUTES: 6,
  
  // 🎁 TEMPLATE PARA COPIAR Y PEGAR (Para usar en el placeholder del textarea)
  JSON_TEMPLATE: `{
  "start": [-16.500000, -68.150000],
  "end": [-16.510000, -68.160000],
  "path": [
    [-16.500000, -68.150000],
    [-16.505000, -68.155000],
    [-16.510000, -68.160000]
  ],
  "elevation_gain": 150,
  "elevation_loss": 145,
  "terrain": "Asfalto y Tierra",
  "est_time": "1h 15min"
}`
};